import { filterTypeToString, toLocale } from "system/libraries/utilities";
import { memo, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import EndpointFilterSelectBlock from "./EndpointFilterSelectBlock";
import EndpointFilterSelected from "./EndpointFilterSelected";
import { useDispatch, useSelector } from "react-redux";
import IRootState from "system/types/store/IRootState";
import { mainActions } from "system/store/slices/main-slice";
import { IMultiFilter } from "system/types/interfaces";
import { useConfiguration, usePageNavigation, useApi, useMultiFilters, useUrlSearch } from "system/hooks";
import { CustomerLicenseDisplayOption } from "system/types/enums/CustomerLicenseDisplayOption";
import { InfoIcon } from "./Icons";
import { Skeleton } from "@mui/material";
let filtersUpdateRefresh: NodeJS.Timeout;

const NumSkeleton = () => (
  <Skeleton
    variant="text"
    width={36}
    sx={{
      display: "inline-block",
      fontSize: "13px",
      bgcolor: "rgba(255, 255, 255, 0.15)",
      "&::after": { background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)" },
    }}
  />
);

interface EndpointFilterProps {
  showTotals?: boolean;
  // Filtered and license counts are tenant-wide and only meaningful on the dashboard.
  reportingCountOnly?: boolean;
}

const EndpointFilter = ({ showTotals = false, reportingCountOnly = false }: EndpointFilterProps) => {
  const api = useApi();
  const dispatch = useDispatch();
  const { navigateToEndpointPage } = usePageNavigation();
  const [hideSelectBlocks] = useState<number[]>([5, 6]);
  const [newValues, setNewValues] = useState<{ type: number; values: string[] }[]>([]);
  const [moreFiltersVisible, setMoreFiltersVisible] = useState(false);
  const [filtersRefresh, setFiltersRefresh] = useState<boolean>(false);
  const { filters, lastFilterSet, filteredEndpointCount, endpointCounts } = useSelector((state: IRootState) => state.main);
  const { currentMultiFilters, setMultiFilter, factsToFilter, clearFilters } = useMultiFilters();
  const { currentTenant } = useConfiguration();

  const urlSearch = useUrlSearch();

  const quickSelectionChangeHandler = async (selection: IMultiFilter[]) => {
    // Typeahead must return exactly 1 element
    if (selection.length !== 1) {
      return;
    }

    const newFilter = selection[0];

    // Check if single filter is a machine name filter
    if (newFilter.filterType === 6 && !currentMultiFilters.filter((x) => x.filterValue).length && newFilter.filterValue) {
      const machineIdResponse = await api.machine.getIdByName(newFilter.filterValue);
      if (machineIdResponse.isSuccess && machineIdResponse.value && +machineIdResponse.value > 0) {
        navigateToEndpointPage(machineIdResponse.value);
        return;
      }
    }

    setMultiFilter(newFilter.filterType, newFilter.filterValue ? [newFilter.filterValue] : []);
  };

  const setFilter = (type: number, values: string[]) => {
    let result = [...newValues];
    let modified = false;
    for (const val of result) {
      if (val.type === type) {
        val.values = values;
        modified = true;
      }
    }

    if (!modified) {
      result.push({ type, values });
    }

    setNewValues(result);
  };

  const getFilterValues = (type: number): string[] => {
    for (const val of newValues) {
      if (val.type === type) {
        return val.values;
      }
    }

    return [];
  };

  const applyFiltersHandler = () => {
    for (const val of newValues) {
      setMultiFilter(val.type, val.values);
    }

    setMoreFiltersVisible(false);
  };

  const clearFilterHandler = (type: number) => {
    setMultiFilter(type, []);
  };

  useEffect(() => {
    const result: { type: number; values: string[] }[] = [];
    for (const name in factsToFilter) {
      result.push({
        type: factsToFilter[name],
        values: urlSearch.getAll(name),
      });
    }

    setNewValues(result);
  }, [currentMultiFilters, moreFiltersVisible, factsToFilter, urlSearch]);


  const showLicensed = currentTenant?.customerShowLicenseFlag !== CustomerLicenseDisplayOption.DoNotShowLicenseCount;

  const hasFiltersApplied = useMemo<boolean>(() => {
    return currentMultiFilters.some((x) => x.filterValue);
  }, [currentMultiFilters]);

  // Get filters every 10 minutes
  useEffect(() => {
    if (filters?.length && new Date().getTime() - lastFilterSet < 600000) {
      return;
    }

    api.metric.filters().then((result) => {
      if (result?.isSuccess && result.items?.length) {
        dispatch(mainActions.setFilters(result.items));
      } else {
        console.warn(result?.failMessage);
      }

      filtersUpdateRefresh = setTimeout(() => {
        setFiltersRefresh(!filtersRefresh);
      }, 600000);
    });

    return () => {
      clearTimeout(filtersUpdateRefresh);
    };
  }, [api, dispatch, filters, lastFilterSet, filtersRefresh, setFiltersRefresh]);

  return filters ? (
    <div style={{ marginBottom: "10px" }}>
      <div className="d-flex rmt-20 flex-wrap gap-1 items-center align-items-center" style={{ marginTop: "25px" }}>
        <div className="search">
          <Typeahead
            id="quick-filter"
            selected={[]}
            onChange={quickSelectionChangeHandler}
            options={filters ?? []}
            labelKey={(x: IMultiFilter) => `${x.filterValue} (${filterTypeToString(x.filterType)})`}
            align="left"
            filterBy={["filterValue"]}
            highlightOnlyResult={true}
            placeholder="Quick filter"
          />
          <i className="bi bi-search"></i>
        </div>
        <div className="btn-more-filter">
          <OverlayTrigger
            placement="bottom"
            show={moreFiltersVisible}
            overlay={
              <Popover id="popover-basic">
                <Popover.Body>
                  {Object.values(factsToFilter)
                    .filter((x) => hideSelectBlocks.indexOf(x) === -1)
                    .map((type, i) => (
                      <EndpointFilterSelectBlock
                        key={i}
                        type={type}
                        values={getFilterValues(type)}
                        options={filters.filter((x) => x.filterType === type)}
                        onValueChange={setFilter}
                      />
                    ))}
                  <div className="row mb-2">
                    <div className="d-flex justify-content-between">
                      <button type="button" className="btn btn-primary" onClick={() => applyFiltersHandler()}>
                        Apply
                      </button>
                      <button type="button" className="btn btn-default" onClick={() => setMoreFiltersVisible(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </Popover.Body>
              </Popover>
            }
          >
            <button type="button" className="btn btn-custom ml-5" onClick={() => setMoreFiltersVisible(!moreFiltersVisible)}>
              <i className="bi bi-filter"></i> Add Filter
            </button>
          </OverlayTrigger>
        </div>
        <div
          className="d-flex flex-column gap-1 flex-grow-1 justify-content-end align-items-end"
          style={{
            color: "var(--title-color)",
            fontSize: "13px",
          }}
        >
          {showTotals && (
            <>
              <div className="d-flex align-items-center gap-1">
                {hasFiltersApplied && !reportingCountOnly && (
                  <>
                    <strong>{filteredEndpointCount !== null ? toLocale(filteredEndpointCount) : <NumSkeleton />}</strong>
                    <span>filtered</span>
                    <InfoIcon text="The number of endpoints represented in the reporting data after applying the specified filters." placement="bottom-end" />
                    <span className="mx-1">/</span>
                  </>
                )}
                <strong>{endpointCounts ? toLocale(endpointCounts.totalReporting) : <NumSkeleton />}</strong>
                <span>total reporting endpoints</span>
                <InfoIcon
                  text={`The number of endpoints represented in the reporting data when no filters are applied. This number is increased when new endpoints run Aiden software and is decreased when endpoints have not run Aiden software for the number of days defined as a decommissioned endpoint (${currentTenant?.decommissionDays ?? 0}). Changing the decommission configuration only affects new reports from endpoints, not existing reporting data.`}
                  placement="bottom-end"
                />
              </div>
              {showLicensed && !hasFiltersApplied && !reportingCountOnly && (
                <div className="d-flex align-items-center gap-1">
                  <strong>{endpointCounts ? toLocale(endpointCounts.licensesInUse) : <NumSkeleton />}</strong>
                  <span>licenses in use</span>
                  <InfoIcon
                    text={`The number of actively used licenses. This number is increased when new endpoints run Aiden software and is decreased when endpoints have not run Aiden software for the number of days defined as a licensed endpoint (${currentTenant?.licenseDays ?? 0}).`}
                    placement="bottom-end"
                  />
                  <span className="mx-1">/</span>
                  <strong>{toLocale(currentTenant?.licensedMachineCount ?? 0)}</strong>
                  <span>total purchased licenses</span>
                  <InfoIcon text="The total number of licenses purchased with all active contracts associated with this account and policy." placement="bottom-end" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className="selected-filters">
        {currentMultiFilters
          .filter((x) => x.filterValue)
          .map((x) => (
            <EndpointFilterSelected key={x.filterType} type={x.filterType} value={x.filterValue} clear={clearFilterHandler} />
          ))}
        {currentMultiFilters.filter((x) => x.filterValue).length > 0 && (
          <span className="close-button">
            <a
              href="/"
              onClick={(event) => {
                clearFilters();
                event.preventDefault();
              }}
            >
              Clear Filters
            </a>
          </span>
        )}
      </div>
    </div>
  ) : (
    <></>
  );
};

export default memo(EndpointFilter);
